import { useState, type ReactElement } from "react";

import { describedBy, errorIdOf, helpIdOf } from "../../forms/described-by.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import type { CenterCancellation } from "./tenant-types.js";
import { adminCenterApi } from "./urls.js";

/** 「日時を指定」を選んだときの <c>timing</c> の値（サーバの AdminCenterApiController.Cancel と同じ）。 */
const scheduled = "at";

/** 日時の欄の値（<c>2026-10-31T23:59</c>）を、ダイアログで読める形（<c>2026/10/31 23:59</c>）にする。 */
const readable = (value: string): string => value.replace("T", " ").replaceAll("-", "/");

/**
 * 最後の確認（重要な操作は最後にダイアログで確かめる。docs/01 §7）。
 * <c>stopAt</c> は日時を指定したときの欄の値、今すぐなら null。
 */
/**
 * 解約で起きること。<b>PAY.JP を使っている契約（定期課金を止める契約）だけ PAY.JP と書く</b>——
 * 請求書払いの契約で書くと混乱する（2026-10-11 に利用者が決めた）。
 */
const whatHappens = (stopsPayjp: boolean): string =>
    stopsPayjp ? "PAY.JP の定期課金を止め、契約者に解約のメールを送ります。" : "契約者に解約のメールを送ります。";

export const centerCancelDialog = (title: string, stopAt: string | null, stopsPayjp: boolean): string =>
    `「${title}」を解約します。${whatHappens(stopsPayjp)}`
    + (stopAt === null || stopAt === ""
        ? "プリザンターはすぐに使えなくなります。"
        : `プリザンターは ${readable(stopAt)}（日本時間）に使えなくなります。`)
    + "この操作は元に戻せません。よろしいですか？";

function CancelForm({ tenantId, title, cancellation }: {
    readonly tenantId: number;
    readonly title: string;
    readonly cancellation: CenterCancellation;
}): ReactElement {
    const [timing, setTiming] = useState("now");
    const form = useApiForm(adminCenterApi.cancel(tenantId), undefined, (valueOf) => centerCancelDialog(
        title, valueOf("timing") === scheduled ? valueOf("stopAt") : null, cancellation.stopsPayjp,
    ));
    const error = form.fieldError("stopAt");
    const invalid = error !== undefined;

    return (
        <form method="post" onSubmit={form.onSubmit} data-center-cancel-form="">
            <FormSummary messages={form.summary} />
            <fieldset className="mb-4">
                <legend className="form-label">プリザンターの利用を止める日時</legend>
                <label className="mb-2 flex items-center gap-2 text-sm">
                    <input
                        type="radio" className="form-check rounded-full" name="timing" value="now"
                        checked={timing === "now"} onChange={() => { setTiming("now"); }}
                    />
                    今すぐ
                </label>
                {cancellation.canSchedule
                    ? (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio" className="form-check rounded-full" name="timing" value={scheduled}
                                checked={timing === scheduled} onChange={() => { setTiming(scheduled); }}
                            />
                            {`日時を指定する（当月末 ${cancellation.latestStopAtLabel} まで）`}
                        </label>
                    )
                    : null}
            </fieldset>
            {timing === scheduled
                ? (
                    <div className="mb-4">
                        <label className="form-label" htmlFor="stopAt">日時（日本時間）</label>
                        <input
                            className={invalid ? "form-input is-invalid" : "form-input"}
                            id="stopAt"
                            name="stopAt"
                            type="datetime-local"
                            min={cancellation.earliestStopAt}
                            max={cancellation.latestStopAt}
                            defaultValue={cancellation.latestStopAt}
                            aria-invalid={invalid ? true : undefined}
                            {...describedBy("stopAt", { help: true, invalid, required: true })}
                        />
                        <p className="form-help" id={helpIdOf("stopAt")}>
                            過去と翌月以降は指定できません。その日時まではプリザンターを使えます（課金と解約の手続きは今行います）。
                        </p>
                        <span
                            className={`form-error ${invalid ? "field-validation-error" : "field-validation-valid"}`}
                            id={errorIdOf("stopAt")}
                            data-valmsg-for="stopAt"
                        >
                            {error}
                        </span>
                    </div>
                )
                : null}
            <button type="submit" className="btn-danger btn-sm" disabled={form.busy}>解約する</button>
        </form>
    );
}

/**
 * 管理センターからの解約（Implem.Portal.Backend の docs/features/36）。<b>契約 1 件の画面のいちばん下</b>に置く。
 * 区画は<b>請求の権限を持つ運営に、解約していない契約でだけ</b>届く（サーバが決める）。
 *
 * 取り消せない操作なので、最初は控えめな赤のボタンだけを出し、押すと日時を選ぶ欄が開く。
 * 送る直前にブラウザの確認ダイアログで聞く。会員の解約と同じ処理（PAY.JP を使う契約なら定期課金を止め、契約者へ解約のメール）。
 */
export function CancelSection({ tenantId, title, cancellation }: {
    readonly tenantId: number;
    /** 契約のシステム名（確認ダイアログの文に入れる）。 */
    readonly title: string;
    readonly cancellation: CenterCancellation;
}): ReactElement {
    const [open, setOpen] = useState(false);

    return (
        <section className="card mb-4 p-6" data-center-cancel="">
            <h2 className="section-title">解約</h2>
            <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-slate-500">
                    {`この契約を解約します。${whatHappens(cancellation.stopsPayjp)}元に戻せません。`}
                </p>
                {open
                    ? null
                    : (
                        <button type="button" className="btn-danger-quiet btn-sm shrink-0" onClick={() => { setOpen(true); }}>
                            解約する
                        </button>
                    )}
            </div>
            {open
                ? (
                    <div className="mt-4">
                        <CancelForm tenantId={tenantId} title={title} cancellation={cancellation} />
                    </div>
                )
                : null}
        </section>
    );
}
